import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { inspect, log } from '@/utils/logger'
import { AvenAdvisorOnboardingV2LiabilityDetectionInfo, AvenAdvisorOnboardingLiabilityQuizJan2026Data } from 'aven_types'
import { appLocalStorage, appSessionStorage, localStorageKey } from '@/utils/storage'
import * as onboardingV2Api from '@/services/onboardingV2'

export const useOnboardingV2Store = defineStore('onboardingV2', () => {
    const advisorLeadLiabilityDetectionStatus = ref<AvenAdvisorOnboardingV2LiabilityDetectionInfo | null>(null)
    const isPullingCreditReportAndDetectingLiabilities = ref<boolean>(false)
    const liabilityDetectionStartTimestamp = ref<number | null>(null)
    const isRecordingLiabilityQuizAnswers = ref<boolean>(false)

    // Computed
    const isAdvisorLeadInOnboardingV2Experiment = computed(() => {
        const experimentsOverrides = JSON.parse(appSessionStorage.getItem(localStorageKey.experimentsOverrides) ?? '{}')
        const visitorIdBasedResult = !!(experimentsOverrides['advisorOnboardingV2'] && experimentsOverrides['advisorOnboardingV2'].includes('showOnboardingV2'))
        const officeIpBasedResult = !!(experimentsOverrides['advisorOnboardingV2OfficeIp'] && experimentsOverrides['advisorOnboardingV2OfficeIp'].includes('showOnboardingV2'))
        // New unified V2 experiment (visitorId-based, gates onboarding for new users)
        const fullFlowV2 = !!experimentsOverrides['advisorV2FullFlow']?.includes('showAdvisorV2')
        // Permanent assignment from tracking table (stored in localStorage)
        const assignedV2 = appLocalStorage.getItem(localStorageKey.assignedV2) === 'true'
        const result = visitorIdBasedResult || officeIpBasedResult || fullFlowV2 || assignedV2
        log.info('isAdvisorLeadInOnboardingV2Experiment: %{result} (visitorId: %{visitorIdBasedResult}, officeIp: %{officeIpBasedResult}, fullFlowV2: %{fullFlowV2}, assignedV2: %{assignedV2})', {
            result,
            visitorIdBasedResult,
            officeIpBasedResult,
            fullFlowV2,
            assignedV2,
        })
        return result
    })

    // Actions
    const pullCreditReportAndDetectLiabilities = async (): Promise<void> => {
        isPullingCreditReportAndDetectingLiabilities.value = true
        liabilityDetectionStartTimestamp.value = Date.now()
        log.info('Pulling credit report and detecting liabilities started at %{timestamp}', { timestamp: liabilityDetectionStartTimestamp.value })
        try {
            const response = await onboardingV2Api.pullCreditReportAndDetectLiabilities()
            advisorLeadLiabilityDetectionStatus.value = response.data.payload
            log.info('Successfully pulled credit report and detected liabilities: currentlyOpenHELOCTradelines: %{openHelocTradelines}, currentlyOpenMortgageTrades: %{openMortgageTrades}', {
                openHelocTradelines: advisorLeadLiabilityDetectionStatus.value?.currentlyOpenHELOCTradelines,
                openMortgageTrades: advisorLeadLiabilityDetectionStatus.value?.currentlyOpenMortgageTrades,
            })
        } catch (error) {
            log.error('Error pulling credit report and detecting liabilities: %{error}', { error })
            throw error
        } finally {
            isPullingCreditReportAndDetectingLiabilities.value = false
        }
    }

    const recordLiabilityQuizV2Answers = async (payload: AvenAdvisorOnboardingLiabilityQuizJan2026Data): Promise<void> => {
        isRecordingLiabilityQuizAnswers.value = true
        try {
            await onboardingV2Api.recordLiabilityQuizV2Answers(payload)
            log.info('Successfully recorded liability quiz V2 answers: %{payload}', { payload: inspect(payload) })
        } catch (error) {
            log.error('Error recording liability quiz V2 answers: %{error}', { error })
            throw error
        } finally {
            isRecordingLiabilityQuizAnswers.value = false
        }
    }

    function reset(): void {
        advisorLeadLiabilityDetectionStatus.value = null
        isPullingCreditReportAndDetectingLiabilities.value = false
        liabilityDetectionStartTimestamp.value = null
        isRecordingLiabilityQuizAnswers.value = false
    }

    return {
        // State
        advisorLeadLiabilityDetectionStatus,
        isPullingCreditReportAndDetectingLiabilities,
        liabilityDetectionStartTimestamp,
        isRecordingLiabilityQuizAnswers,

        // Computed
        isAdvisorLeadInOnboardingV2Experiment,

        // Actions
        pullCreditReportAndDetectLiabilities,
        recordLiabilityQuizV2Answers,
        reset,
    }
})
